import { useCallback } from 'react';
import useSessionPersistedState from '../../hooks/useSessionPersistedState';

const DEFAULT_DATE_RANGE = {
  created_on: {
    type: 'date',
    start: null,
    end: null,
  },
};

const DEFAULT_PAGINATION = {
  page: 0,
  size: 10,
};

const CATEGORY_FILTER_MAPPING = {
  ALL: {},

  SALES_DOCUMENTS: {
    category: 'E_INVOICE',
    subCategory: 'SALES',
  },

  PURCHASE_DOCUMENTS: {
    category: 'E_INVOICE',
    subCategory: 'PURCHASES',
  },

  CUSTOMER: {
    category: 'CUSTOMER',
  },

  SUPPLIER: {
    category: 'SUPPLIER',
  },

  PRODUCT: {
    category: 'PRODUCT',
  },
};

const useBulkUploadFilters = ({ persistKey } = {}) => {
  const [selectedFilter, setSelectedFilter] = useSessionPersistedState(
    persistKey ? `${persistKey}:selectedFilter` : undefined,
    'filename',
  );

  const [dateRange, setDateRange] = useSessionPersistedState(
    persistKey ? `${persistKey}:dateRange` : undefined,
    DEFAULT_DATE_RANGE,
  );

  const [selectedStatus, setSelectedStatus] = useSessionPersistedState(
    persistKey ? `${persistKey}:selectedStatus` : undefined,
    '',
  );

  const [selectedCategory, setSelectedCategory] = useSessionPersistedState(
    persistKey ? `${persistKey}:selectedCategory` : undefined,
    'ALL',
  );

  const [pagination, setPagination] = useSessionPersistedState(
    persistKey ? `${persistKey}:pagination` : undefined,
    DEFAULT_PAGINATION,
  );

  const [searchTerm, setSearchTerm] = useSessionPersistedState(persistKey ? `${persistKey}:searchTerm` : undefined, '');

  const buildQuery = useCallback(() => {
    const query = {
      ...pagination,
    };

    if (searchTerm) {
      query[selectedFilter] = searchTerm;
    }

    if (selectedStatus) {
      query.status = selectedStatus;
    }

    if (dateRange?.created_on?.start || dateRange?.created_on?.end) {
      const { start, end } = dateRange.created_on;

      query.created_on = JSON.stringify({
        ...(start && { start }),
        ...(end && { end }),
      });
    }

    return {
      ...query,
      ...(CATEGORY_FILTER_MAPPING[selectedCategory] ?? {}),
    };
  }, [pagination, searchTerm, selectedFilter, selectedStatus, dateRange, selectedCategory]);

  const handleFilterChange = (event) => {
    setSelectedFilter(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  const handlePaginationChange = (page, limit) => {
    setPagination({
      page,
      size: limit,
    });
  };

  const handlePaginationReset = () => {
    setPagination((currentPagination) => ({
      ...currentPagination,
      page: 0,
    }));
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);

    setPagination((currentPagination) => ({
      ...currentPagination,
      page: 0,
    }));
  };
  const currentQuery = buildQuery();

  return {
    searchTerm,
    selectedStatus,
    selectedFilter,
    selectedCategory,
    currentQuery,
    dateRange,

    handleFilterChange,
    handleSearchChange,
    handleDateRangeChange,
    handlePaginationChange,
    handlePaginationReset,
    handleStatusChange,
    handleCategoryChange,
  };
};

export default useBulkUploadFilters;
